import { Context, Data, Effect, Layer, Stream } from 'effect'
import { SdkClient } from './sdk-client'
import { XhrUpload } from './xhr-upload'

export class FileStorageUploadTransientError extends Data.TaggedError(
  'FileStorageUploadTransientError'
)<{ cause: unknown }> {}

export class FileStorageUrlTransientError extends Data.TaggedError('FileStorageUrlTransientError')<{
  cause: unknown
}> {}

type FileStorageService = {
  upload: (
    file: File,
    options: { destinationPath: string; mimeType?: string }
  ) => Effect.Effect<void, FileStorageUploadTransientError>
  resolveURL: (path: string) => Effect.Effect<string, FileStorageUrlTransientError>
}

export class FileStorage extends Context.Service<FileStorage, FileStorageService>()(
  'FileStorage'
) {}

/**
 * Storage is application orchestration, not an environment adapter. Every
 * environment runs this same program; only SDK HTTP and the presigned PUT
 * transport beneath it are substitutable.
 */
export function fileStorageLayer() {
  return Layer.effect(
    FileStorage,
    Effect.gen(function* () {
      const sdk = yield* SdkClient
      const upload = yield* XhrUpload

      return {
        upload: (file, options) =>
          Effect.gen(function* () {
            const presigned = yield* sdk
              .send({
                type: 'storage/generatePresignedUploadURL',
                filename: file.name,
                filepath: options.destinationPath,
                mimeType: options.mimeType
              })
              .pipe(
                Effect.catchTags({
                  TransportError: (cause) =>
                    Effect.fail(new FileStorageUploadTransientError({ cause })),
                  TimeoutError: (cause) =>
                    Effect.fail(new FileStorageUploadTransientError({ cause })),
                  HttpStatusError: (cause) => Effect.die(cause)
                })
              )

            yield* upload
              .putPresignedUrl({
                url: presigned.uploadUrl,
                headers: presigned.uploadHeaders,
                file
              })
              .pipe(
                Stream.runDrain,
                Effect.catchTags({
                  NetworkError: (cause) =>
                    Effect.fail(new FileStorageUploadTransientError({ cause })),
                  HttpError: (cause) => Effect.die(cause)
                })
              )
          }),
        resolveURL: (path) => {
          if (!path.startsWith('storage://')) {
            return Effect.succeed(path)
          }

          return sdk
            .send({
              type: 'storage/generatePresignedGetURL',
              filepath: path.slice('storage://'.length)
            })
            .pipe(
              Effect.map((response) => response.url),
              Effect.catchTags({
                TransportError: (cause) => Effect.fail(new FileStorageUrlTransientError({ cause })),
                TimeoutError: (cause) => Effect.fail(new FileStorageUrlTransientError({ cause })),
                HttpStatusError: (cause) => Effect.die(cause)
              })
            )
        }
      } satisfies FileStorageService
    })
  )
}
